import { useMemo, useRef, useEffect, useState } from 'react';

import { useMenu } from 'global/MenuContext';
import { useMenuSelection } from 'global/MenuSelectionContext';
import { useUser } from 'global/UserContext';
import { logger } from 'utils/logger';

/**
 * Centralized hook to manage the application initialization state
 * Avoids race conditions between different contexts during loading
 */
export const useAppInitialization = () => {
  const { token, user, isLoading: userLoading } = useUser();
  const { menu, isLoading: menuLoading } = useMenu();
  const { isInitialized: menuSelectionInitialized } = useMenuSelection();

  // State to control whether the first initialization has completed
  const [hasInitialized, setHasInitialized] = useState(false);
  const initializationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Unified initialization state
  const isFullyInitialized = useMemo(() => {
    const hasToken = !!token;
    const hasUser = !!user;
    const hasMenu = menu.length > 0;
    const isUserReady = !userLoading;
    const isMenuReady = !menuLoading;
    const isMenuSelectionReady = menuSelectionInitialized;

    const fullyInitialized =
      hasToken && hasUser && hasMenu && isUserReady && isMenuReady && isMenuSelectionReady;

    logger.debug('[useAppInitialization] Initialization state:', {
      hasToken,
      hasUser,
      hasMenu,
      isUserReady,
      isMenuReady,
      isMenuSelectionReady,
      fullyInitialized,
      hasInitialized,
      menuLength: menu.length,
      currentPath: window.location.pathname,
    });

    return fullyInitialized;
  }, [
    token,
    user,
    userLoading,
    menu.length,
    menuLoading,
    menuSelectionInitialized,
    hasInitialized,
  ]);

  // Effect to mark when initialization has completed
  useEffect(() => {
    logger.debug('[useAppInitialization] Checking initialization:', {
      isFullyInitialized,
      hasInitialized,
      token: !!token,
      user: !!user,
      userLoading,
      menuLength: menu.length,
      menuLoading,
      menuSelectionInitialized,
    });

    if (isFullyInitialized && !hasInitialized) {
      logger.debug('[useAppInitialization] ✅ Full initialization detected!');
      setHasInitialized(true);
    }
  }, [
    isFullyInitialized,
    hasInitialized,
    token,
    user,
    userLoading,
    menu.length,
    menuLoading,
    menuSelectionInitialized,
  ]);

  // Safety timeout to prevent hanging
  useEffect(() => {
    if (!hasInitialized) {
      initializationTimeoutRef.current = setTimeout(() => {
        logger.warn(
          '[useAppInitialization] ⚠️ Initialization timeout reached, forcing initialization'
        );
        setHasInitialized(true);
      }, 10000); // 10 segundos de timeout
    }

    return () => {
      if (initializationTimeoutRef.current) {
        clearTimeout(initializationTimeoutRef.current);
      }
    };
  }, [hasInitialized]);

  // Detailed loading state for debug
  const loadingStates = useMemo(
    () => ({
      userLoading,
      menuLoading,
      menuSelectionInitialized,
      hasToken: !!token,
      hasUser: !!user,
      hasMenu: menu.length > 0,
      // Additional states for better control
      isUserContextReady: !userLoading && !!token && !!user,
      isMenuContextReady: !menuLoading && menu.length > 0,
      isMenuSelectionReady: menuSelectionInitialized,
    }),
    [userLoading, menuLoading, menuSelectionInitialized, token, user, menu.length]
  );

  return {
    isFullyInitialized,
    hasInitialized,
    loadingStates,
    // Individual states for specific cases
    isUserReady: !userLoading && !!token && !!user,
    isMenuReady: !menuLoading && menu.length > 0,
    isMenuSelectionReady: menuSelectionInitialized,
  };
};
